import './otel-setup'; // Ensure this is imported first
import "./App.css";
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import RegistrationForm from './components/RegistrationForm';
import RequestRegistrationForm from './components/RequestRegistrationForm';
import LoginForm from './components/LoginForm';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import ResetPasswordForm from './components/ResetPasswordForm';
import MenuComponent from './components/MenuComponent';
import DemoPage, { useDemoEmbedUrl } from './components/DemoPage';
import WhyPage from './components/WhyPage';
import WhyCategoryPage from './components/WhyCategoryPage';
import WhyProspectPage from './components/WhyProspectPage';
import { Why1CategoryPage, Why1ProspectPage } from './components/Why1Page';
import ClaimPage from './components/ClaimPage';
import EnvBanner from './components/EnvBanner';
import SearchComponent from './components/generalSearch/SearchComponent';
import { LoginResponse } from './types/Auth';
import { parseAuthorities } from './utils/jwt';

/** Reads and validates the saved auth token from localStorage. */
function loadSavedToken(): string {
    const savedToken = localStorage.getItem('bh_auth_token');
    const expiresAtRaw = localStorage.getItem('bh_auth_expires_at');
    const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : 0;
    if (!savedToken || !expiresAt || Number.isNaN(expiresAt) || Date.now() >= expiresAt) {
        localStorage.removeItem('bh_auth_token');
        localStorage.removeItem('bh_auth_expires_at');
        return '';
    }
    return savedToken;
}

const HomeSection: React.FC = () => {
    const [token, setToken] = useState('');
    const [authorities, setAuthorities] = useState<string[]>([]);
    const demoAvailable = !!useDemoEmbedUrl();

    const isLoggedIn = useMemo(() => token.trim().length > 0, [token]);

    useEffect(() => {
        const saved = loadSavedToken();
        if (saved) {
            setToken(saved);
            setAuthorities(parseAuthorities(saved));
        }
        // Visit-analytics beacon: fire once per app load; must never disturb the app.
        import('./api/AnalyticsAPI')
            .then(({ postVisit }) => postVisit(saved || undefined))
            .catch(() => {});
    }, []);

    const handleLogout = () => {
        setToken('');
        setAuthorities([]);
        localStorage.removeItem('bh_auth_token');
        localStorage.removeItem('bh_auth_expires_at');
    };

    // Signed in: the full application.
    if (isLoggedIn) {
        return (
            <div className="app-layout">
                <MenuComponent token={token} authorities={authorities} onLogout={handleLogout} />
            </div>
        );
    }

    // Public landing: anyone can search; login / register offered as links.
    return (
        <div className="app-layout">
            <div className="public-topbar">
                <strong className="public-brand">BabaHanks</strong>
                <div className="public-links">
                    <Link to="/why" className="btn-outline">Why BabaHanks</Link>
                    {demoAvailable && <Link to="/demo" className="btn-outline">Demo</Link>}
                    <Link to="/login" className="btn-outline">Login</Link>
                    <Link to="/register" className="btn-outline">Create Account</Link>
                </div>
            </div>
            <div className="public-body">
                <SearchComponent token={null} />
            </div>
        </div>
    );
};

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [authMessage, setAuthMessage] = useState('');

    useEffect(() => {
        if ((location.state as any)?.registrationSuccess) {
            setAuthMessage('Registration successful. Please sign in.');
        }
    }, [location.state]);

    const handleLoginSuccess = (auth: LoginResponse) => {
        const safeExpiresIn = Number(auth.expiresIn);
        const expiresAt = Date.now() + (Number.isFinite(safeExpiresIn) ? safeExpiresIn * 1000 : 60 * 60 * 1000);
        localStorage.setItem('bh_auth_token', auth.token);
        localStorage.setItem('bh_auth_expires_at', String(expiresAt));
        navigate('/');
    };

    return (
        <div className="app-container">
            <div className="auth-container">
                <div className="auth-wrapper">
                    <LoginForm
                        onLoginSuccess={handleLoginSuccess}
                        onSwitchToRegister={() => {}}
                    />
                    <div className="auth-divider">
                        <div className="auth-divider-content">
                            <p>Don't have an account?</p>
                            <Link to="/register" className="btn-outline">Create Account</Link>
                            <Link to="/" className="btn-outline">Back to Search</Link>
                            {authMessage && <p className="success-message">{authMessage}</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
        <div className="app-container">
            <div className="auth-container">
                <div className="auth-wrapper">
                    {children}
                    <div className="auth-divider">
                        <div className="auth-divider-content">
                            <p>Already have an account?</p>
                            <Link to="/login" className="btn-outline">Sign In</Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
);

const App: React.FC = () => (
    <BrowserRouter>
        <EnvBanner />
        <Routes>
            <Route path="/" element={<HomeSection />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/demo/:slug" element={<DemoPage />} />
            <Route path="/why" element={<WhyPage />} />
            {/* /why/... serves the form-first design (every sent link lands
                here); the original three-step page stays reachable at
                /why0/... until we're sure (also tagged why-page-v1). */}
            <Route path="/why/c/:categoryId" element={<Why1CategoryPage />} />
            <Route path="/why/p/:token" element={<Why1ProspectPage />} />
            <Route path="/why0/c/:categoryId" element={<WhyCategoryPage />} />
            <Route path="/why0/p/:token" element={<WhyProspectPage />} />
            {/* /why1 links already shared keep working. */}
            <Route path="/why1/c/:categoryId" element={<Why1CategoryPage />} />
            <Route path="/why1/p/:token" element={<Why1ProspectPage />} />
            {/* A business takes ownership of its managed org (public). */}
            <Route path="/claim/:token" element={<ClaimPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<AuthLayout><RequestRegistrationForm /></AuthLayout>} />
            <Route path="/register-complete" element={<AuthLayout><RegistrationForm /></AuthLayout>} />
            <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordForm /></AuthLayout>} />
            <Route path="/reset-password" element={<AuthLayout><ResetPasswordForm /></AuthLayout>} />
        </Routes>
    </BrowserRouter>
);

export default App;